// JOBE-0059.11: operator console — per-channel summary dashboard.
// Read-only view using the same polling pattern and table styling as
// DispatcherConsole (JOBE-0056.10).
//
// JOBE-0117: the sibling TaskSummary export (and the /admin/tasks page it
// backed) was removed — its use case is now served by the Project Home
// Tasks sub-tab (JOBE-0106). The operator endpoint
// `/api/admin/tasks/summary` is unaffected.
// JOBE-0084: the sibling AgentSummary export was replaced by AgentsList in
// AgentsPage.jsx (wireframe §3). The underlying /api/admin/agents/summary
// endpoint is still consumed there.
//
// Empty states are intentional and load-bearing — no synthetic rows.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const POLL_INTERVAL_MS = 5000;

// formatTime converts an RFC3339 string to a locale date+time string.
// Uses toLocaleString() rather than toLocaleTimeString() so timestamps that
// span multiple days (lastActiveAt, lastActivityAt, dueAt) display the date.
function formatTime(t) {
  if (!t) return '—';
  try {
    return new Date(t).toLocaleString();
  } catch (_e) {
    return t;
  }
}

// useSummaryList is a generic polling hook for an admin summary endpoint.
// Returns { rows, error, loading }.
//
// Uses a recursive setTimeout instead of setInterval so a slow response
// never causes overlapping concurrent requests — the next poll is
// scheduled only after the previous one settles (success or error).
function useSummaryList(path) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let timerId = null;

    const schedule = () => {
      timerId = setTimeout(run, POLL_INTERVAL_MS);
    };

    const run = () => {
      fetch(path, { credentials: 'same-origin', signal: ctrl.signal })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((j) => {
          setRows(Array.isArray(j) ? j : []);
          setError(null);
          schedule();
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          setError(err.message);
          schedule();
        });
    };

    run();
    return () => { ctrl.abort(); clearTimeout(timerId); };
  }, [path]);

  return { rows, error, loading: rows === null };
}

// SummaryShell wraps the loading/error/empty states so each page component
// only needs to render the table body.
function SummaryShell({ title, loading, error, empty, children }) {
  if (error) {
    return (
      <div className="card">
        <h3>{title}</h3>
        <p className="errorMsg">Failed to load: {error}</p>
      </div>
    );
  }
  if (loading) {
    return <div className="card"><h3>{title}</h3><p>Loading…</p></div>;
  }
  if (empty) {
    return (
      <div className="card dispatcher-empty">
        <h3>{title}</h3>
        <p>No data yet.</p>
        <p className="dispatcher-empty-hint">
          Data will appear here once agents are active and sending events.
        </p>
      </div>
    );
  }
  return <div className="card">{children}</div>;
}

// ---- ChannelSummary ----

export function ChannelSummary() {
  const { rows, error, loading } = useSummaryList('/api/admin/channels/summary');

  return (
    <SummaryShell
      title="Channels · summary"
      loading={loading}
      error={error}
      empty={!loading && !error && rows && rows.length === 0}
    >
      <h3>Channels · summary</h3>
      <table className="dispatcher-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row) => (
            <tr key={row.id}>
              <td>
                <Link to={`/chat?channel=${encodeURIComponent(row.id)}`}>
                  {row.id}
                </Link>
              </td>
              <td>{formatTime(row.lastActivityAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </SummaryShell>
  );
}
